import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Flame, Trophy, Users, Dumbbell, Instagram, Play, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { GreekKeyDivider } from "@/components/GreekKeyDivider";
import { GreekWatermark } from "@/components/GreekWatermark";
import { Marquee } from "@/components/Marquee";
import { ElysiumStandard } from "@/components/home/ElysiumStandard";
import { IsItForMe } from "@/components/home/IsItForMe";
import { MemberStories } from "@/components/home/MemberStories";
import { FAQSection } from "@/components/home/FAQSection";

const HERO_VIDEO = "/videos/gym-training-loop.mp4";
const HERO_POSTER = "/videos/gym-training-loop-poster.jpg";
const CLASSES_IMG = "/blog/member-sled-pull.jpg";
const FACILITY_IMG = "/why-elysium/group-agility-drill.jpg";
const ROWING_IMG = "/why-elysium/community-huddle.jpg";
const SIGN_IMG = "/blog/elysium-sigma-signage.jpg";

const INSTAGRAM_URL = "https://www.instagram.com/elysiumtrainingclub";

const COMMUNITY_SHOTS = [
  { type: "image", img: "/why-elysium/coaches-laughing.jpg", ratio: "aspect-[4/5]" },
  { type: "video", video: HERO_VIDEO, poster: HERO_POSTER, ratio: "aspect-[9/16]" },
  { type: "image", img: "/blog/elysium-sigma-signage.jpg", ratio: "aspect-square" },
  { type: "image", img: "/founders/member-female-lift.jpg", ratio: "aspect-[4/5]" },
];

const PROGRAMS = [
  { icon: Flame, title: "Functional Fitness", desc: "Daily programmed conditioning that never repeats the same way twice." },
  { icon: Dumbbell, title: "Olympic Lifting", desc: "Technical barbell work coached by a former competitive weightlifter." },
  { icon: Trophy, title: "Super Saturday", desc: "A bigger, social team session to close out the training week." },
  { icon: Users, title: "Engine", desc: "Sunday conditioning built to build your aerobic base." },
];

export default function Home() {
  return (
    <div>
      <section className="relative min-h-[92vh] flex items-end sm:items-center overflow-hidden">
        <div className="absolute top-0 inset-x-0 h-3 sm:h-5 bg-void z-20" />
        <div className="absolute bottom-0 inset-x-0 h-3 sm:h-5 bg-void z-20" />
        <motion.div
          className="absolute inset-0"
          initial={{ scale: 1.15, x: 0, y: 0 }}
          animate={{ scale: 1, x: -10, y: -6 }}
          transition={{ duration: 14, ease: [0.16, 1, 0.3, 1] }}
        >
          <video
            src={HERO_VIDEO}
            poster={HERO_POSTER}
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover object-[50%_20%]"
            data-testid="hero-video"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-void/95 via-void/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-void/55 via-transparent to-void/35" />
          <div className="absolute inset-0" style={{ boxShadow: "inset 0 0 220px 60px rgba(7,6,4,0.85)" }} />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-0 pt-32 sm:pt-0 w-full">
          <GreekWatermark letter="Ε" className="-top-20 -left-8 sm:-left-14" />
          <p className="font-accent italic text-gold text-lg sm:text-xl mb-4 tracking-wide" data-testid="hero-tagline">
            The afterlife for heroes.
          </p>
          <h1
            className="font-greek uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05] max-w-3xl text-balance bg-gradient-to-b from-[#f6ecd2] via-gold to-[#7a6a48] bg-clip-text text-transparent drop-shadow-[0_4px_30px_rgba(154,137,104,0.4)]"
            data-testid="hero-heading"
          >
            Train like your last rep is the one that counts.
          </h1>
          <p className="text-smoke text-base sm:text-lg mt-6 max-w-xl leading-relaxed">
            Elysium is a Functional Fitness gym built for athletes who want more than a workout — programming, coaching,
            and a community that pushes you past who you were yesterday.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mt-9">
            <Button asChild size="lg" className="rounded-none bg-gold text-void hover:bg-gold-hover font-bold uppercase tracking-wider h-14 px-8" data-testid="hero-book-class-button">
              <Link to="/schedule#interest-form">Book a Class <ArrowRight className="h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-none border-gold text-gold hover:bg-gold hover:text-void h-14 px-8" data-testid="hero-view-membership-button">
              <Link to="/membership">View Membership</Link>
            </Button>
          </div>
        </div>
        <GreekKeyDivider className="absolute bottom-0 left-0 z-10" />
      </section>

      <div className="mt-10 sm:mt-14">
        <Marquee bgImage={SIGN_IMG} />
      </div>

      <ElysiumStandard />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 relative">
        <GreekWatermark letter="Α" className="top-0 right-0 sm:right-10" />
        <Reveal>
          <p className="font-accent italic text-gold text-lg mb-3">The arena</p>
          <h2 className="font-anton uppercase tracking-tighter text-cream text-4xl sm:text-5xl max-w-2xl">
            12,000 sq ft of purpose-built iron and space
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-14">
          <Reveal delay={0.05} className="lg:col-span-7">
            <div className="relative h-[420px] sm:h-[520px] overflow-hidden border border-line group" data-testid="home-arena-facility-card">
              <img src={FACILITY_IMG} alt="Inside the Elysium gym" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-void via-void/30 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6 sm:p-8">
                <p className="font-accent italic text-gold text-base mb-2">Inside the gym</p>
                <h3 className="font-anton uppercase text-cream text-2xl sm:text-3xl leading-tight max-w-sm">
                  Olympic platforms, rig wall &amp; recovery corner
                </h3>
                <Button asChild size="sm" className="rounded-none bg-gold text-void hover:bg-gold-hover font-bold uppercase tracking-wider mt-5" data-testid="home-take-tour-button">
                  <Link to="/contact">Schedule a Tour</Link>
                </Button>
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-5 flex flex-col gap-6">
            <Reveal delay={0.1}>
              <div className="relative h-[190px] sm:h-[240px] overflow-hidden border border-line group" data-testid="home-arena-rowing-card">
                <img src={ROWING_IMG} alt="Elysium members and coaches after a session" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-void/70 via-transparent to-transparent" />
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="h-[190px] sm:h-[240px] bg-surface border border-gold/25 p-6 sm:p-7 flex flex-col justify-center" data-testid="home-arena-quote-card">
                <Quote className="h-6 w-6 text-gold mb-3" />
                <p className="font-accent italic text-cream text-lg leading-snug">
                  "Knowledgeable coaches who genuinely care about helping everyone improve."
                </p>
                <p className="text-smoke text-xs uppercase tracking-widest mt-3">— Nia, Elysium Member</p>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-6">
          {PROGRAMS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.06}>
              <div className="bg-void border border-line hover:border-gold/50 transition-colors p-6 h-full" data-testid={`home-program-card-${i}`}>
                <p.icon className="h-7 w-7 text-gold mb-4" />
                <h3 className="font-anton uppercase text-cream text-base sm:text-lg mb-1.5">{p.title}</h3>
                <p className="text-smoke text-sm leading-relaxed">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <Link to="/programs" className="inline-flex items-center gap-2 text-gold uppercase tracking-wider text-sm mt-10 hover:gap-3 transition-all" data-testid="home-see-all-programs-link">
            See all programs <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>

      <IsItForMe />

      <GreekKeyDivider />

      <section className="py-24 sm:py-32 relative">
        <GreekWatermark letter="Σ" className="top-0 left-0 sm:left-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="font-accent italic text-gold text-lg mb-3">Straight from the floor</p>
            <h2 className="font-anton uppercase tracking-tighter text-cream text-4xl sm:text-5xl">Follow the journey</h2>
            <p className="text-smoke mt-4">Real sessions, real athletes — pulled straight from our Instagram, @elysiumtrainingclub.</p>
          </Reveal>

          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noreferrer"
            className="md:hidden mt-8 flex items-center justify-center gap-2 border border-gold/40 bg-surface px-5 py-3 hover:border-gold transition-colors"
            data-testid="home-instagram-follow-link-mobile"
          >
            <Instagram className="h-4 w-4 text-gold" />
            <span className="text-gold uppercase tracking-wider text-sm">Follow @elysiumtrainingclub</span>
          </a>

          <div className="relative mt-14">
            <div className="columns-2 sm:columns-3 gap-5 sm:gap-6 [column-fill:balance]">
              {COMMUNITY_SHOTS.map((shot, i) => (
                <Reveal key={shot.img || shot.video} delay={i * 0.08} className="mb-5 sm:mb-6 break-inside-avoid">
                  <a
                    href={INSTAGRAM_URL}
                    target="_blank"
                    rel="noreferrer"
                    className={`group relative block w-full overflow-hidden border border-line hover:border-gold/50 transition-colors ${shot.ratio}`}
                    data-testid={`home-community-shot-${i}`}
                  >
                    {shot.type === "video" ? (
                      <video
                        src={shot.video}
                        poster={shot.poster}
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <img src={shot.img} alt="Elysium community" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    )}
                    <div className="absolute top-3 right-3 h-9 w-9 rounded-full bg-void/60 border border-gold/40 flex items-center justify-center backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity">
                      {shot.type === "video" ? <Play className="h-3.5 w-3.5 text-gold fill-gold" /> : <Instagram className="h-3.5 w-3.5 text-gold" />}
                    </div>
                  </a>
                </Reveal>
              ))}
            </div>

            <div className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
              <Reveal delay={0.3} className="bg-void/85 backdrop-blur-2xl border border-gold/40 px-8 py-7 text-center" style={{ boxShadow: "0 0 60px 20px rgba(7,6,4,0.7)" }}>
                <Play className="h-5 w-5 text-gold fill-gold mx-auto mb-3" />
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-gold uppercase tracking-wider text-sm hover:gap-3 transition-all"
                  data-testid="home-instagram-follow-link"
                >
                  Follow the Journey <ArrowRight className="h-4 w-4" />
                </a>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <MemberStories />

      <FAQSection />

      <section className="relative py-28 sm:py-36 overflow-hidden">
        <div className="absolute inset-0">
          <img src={CLASSES_IMG} alt="Weights" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-void/70" />
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, rgba(154,137,104,0.18), transparent 65%)" }} />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Reveal>
            <p className="font-accent italic text-gold text-lg mb-3">Your first step</p>
            <h2 className="font-anton uppercase tracking-tighter text-cream text-4xl sm:text-6xl">
              Ready to start?
            </h2>
            <p className="text-smoke mt-6 text-base sm:text-lg max-w-xl mx-auto">
              Book your first free coached session today and experience Elysium for yourself.
            </p>
            <Button asChild size="lg" className="rounded-none bg-gold text-void hover:bg-gold-hover font-bold uppercase tracking-wider h-14 px-10 mt-9" data-testid="home-final-cta-button">
              <Link to="/contact">Book Your First Free Coached Session</Link>
            </Button>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
